import { useState, useEffect, useMemo } from 'react';
import api, { downloadCsv, exportIdsParam } from '../utils/api';
import ClearFiltersButton from '../components/common/ClearFiltersButton';
import { useAuth } from '../context/AuthContext';
import { useUrlParams } from '../hooks/useUrlState';
import Pagination from '../components/Pagination';
import Badge from '../components/Badge';
import ActionsDropdown from '../components/ActionsDropdown';
import StateBlock from '../components/StateBlock';
import CsvImportModal from '../components/CsvImportModal';
import ContractorPanel from '../components/ContractorPanel';
import ContractorEditDrawer from '../components/ContractorEditDrawer';
import SortTh, { useSort } from '../components/common/SortableHeader';
import AlphaFilter from '../components/common/AlphaFilter';

const ENGAGEMENT_LABELS = {
  contract_placement: 'Contract Placement',
  recurring_referral: 'Recurring Referral',
  fte_placement: 'FTE Placement',
};

function isVendorSourced(label) {
  if (!label) return false;
  return /vendor|firm|company|official/i.test(label);
}

function formatEmailCell(c) {
  if (!c.email) return '—';
  if (isVendorSourced(c.primary_email_label)) {
    return (
      <span>
        {c.email} <span className="badge badge-warning" style={{ marginLeft: 4 }}>via vendor</span>
      </span>
    );
  }
  return c.email;
}

export default function ContractorsPage() {
  const { user } = useAuth();
  const [contractors, setContractors] = useState(null);
  const [error, setError] = useState(false);
  const { get, set } = useUrlParams();
  const search = get('search', '');
  const alphaFilter = get('alpha', '');
  const statusFilter = get('status', 'all');
  const subtypeFilter = get('subtype', 'all');
  const page = get('page', 1);
  const pageSize = get('pageSize', 25);
  const [panelId, setPanelId] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const { sortKey, sortDir, handleSort, sortData } = useSort('last_name', 'asc');

  function load() {
    setError(false);
    api.get('/contractors')
      .then(r => setContractors(r.data))
      .catch(() => setError(true));
  }

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!contractors) return [];
    return contractors.filter(c => {
      const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
      if (search && !fullName.includes(search.toLowerCase()) && !(c.email || '').toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter === 'active' && c.computed_status !== 'active') return false;
      if (statusFilter === 'inactive' && c.computed_status !== 'inactive') return false;
      if (subtypeFilter !== 'all' && c.contractor_subtype !== subtypeFilter) return false;
      if (alphaFilter && (c.first_name || '').charAt(0).toUpperCase() !== alphaFilter) return false;
      return true;
    });
  }, [contractors, search, statusFilter, subtypeFilter, alphaFilter]);

  const sorted = useMemo(() => {
    return sortData(filtered, {
      last_name: (c) => `${c.first_name} ${c.last_name}`,
      email: (c) => c.email || '',
      work_authorization: (c) => c.work_authorization || '',
      computed_status: (c) => c.computed_status || '',
    });
  }, [filtered, sortKey, sortDir]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  function handleFilterChange(key, value) {
    set({ [key]: value, page: 1 });
  }

  async function handleExport() {
    await downloadCsv('/contractors/export', 'contractors-export.csv', exportIdsParam(filtered, contractors));
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            Contractor Management {contractors && <span className="page-title-count">({contractors.length} total)</span>}
          </h1>
          <p className="page-subtitle">Manage contractor profiles, work authorizations, and engagement status.</p>
        </div>
        <div className="page-actions">
          {user?.role === 'admin' && (
            <button className="btn btn-secondary" onClick={handleExport}>↓ Export CSV</button>
          )}
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>↑ Upload CSV</button>
          <button className="btn btn-primary" onClick={() => setEditTarget({})}>+ Add Contractor</button>
        </div>
      </div>

      <div className="filter-bar">
        <input
          type="text"
          className="filter-search"
          placeholder="Search contractors by name or email…"
          value={search}
          onChange={e => handleFilterChange('search', e.target.value)}
        />
        <select value={statusFilter} onChange={e => handleFilterChange('status', e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={subtypeFilter} onChange={e => handleFilterChange('subtype', e.target.value)}>
          <option value="all">All Types</option>
          <option value="corp_to_corp">Corp-to-Corp</option>
          <option value="w2">W2</option>
          <option value="1099">1099</option>
        </select>
        <ClearFiltersButton
          active={!!(search || alphaFilter || statusFilter !== 'all' || subtypeFilter !== 'all')}
          onClear={() => set({ search: '', alpha: '', status: '', subtype: '', page: 1 })}
        />
      </div>

      <AlphaFilter items={contractors || []} nameKey="first_name" value={alphaFilter} onChange={l => handleFilterChange('alpha', l)} />

      {error && <StateBlock type="error" action={<button className="btn btn-primary" onClick={load}>Try again</button>} />}

      {!error && contractors === null && <StateBlock type="loading" />}

      {!error && contractors !== null && filtered.length === 0 && (
        <StateBlock type="noResults" message="No contractors match this search. Clear filters or add a contractor." />
      )}

      {!error && filtered.length > 0 && (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <SortTh label="Name" sortKey="last_name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <th>Active Engagement</th>
                <SortTh label="Email" sortKey="email" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Work Auth" sortKey="work_authorization" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortTh label="Status" sortKey="computed_status" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.map(c => (
                <tr key={c.id}>
                  <td>
                    <button className="row-name-link" onClick={() => setPanelId(c.id)}>
                      {c.first_name} {c.last_name}
                    </button>
                  </td>
                  <td>
                    {c.active_engagement_types
                      ? c.active_engagement_types.split(',').map(t => (
                        <Badge key={t} variant="info">{ENGAGEMENT_LABELS[t] || t}</Badge>
                      ))
                      : <Badge variant="neutral">None</Badge>}
                  </td>
                  <td>{formatEmailCell(c)}</td>
                  <td>{c.work_authorization || <Badge variant="warning">Missing</Badge>}</td>
                  <td><Badge variant={c.computed_status === 'active' ? 'success' : 'neutral'}>{c.computed_status === 'active' ? 'Active' : 'Inactive'}</Badge></td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionsDropdown actions={[
                      { label: 'View Profile', onClick: () => setPanelId(c.id) },
                      { label: 'Edit', onClick: () => setEditTarget(c) },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={(p) => set({ page: p })}
            onPageSizeChange={(s) => set({ pageSize: s, page: 1 })}
          />
        </>
      )}

      {panelId && (
        <ContractorPanel
          contractorId={panelId}
          onClose={() => setPanelId(null)}
          onEdit={(data) => { setPanelId(null); setEditTarget(data); }}
        />
      )}

      {editTarget !== null && (
        <ContractorEditDrawer
          contractor={editTarget.id ? editTarget : null}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); load(); }}
        />
      )}

      {showImport && (
        <CsvImportModal
          entityLabel="Contractor"
          importPath="/contractors/import"
          onClose={() => setShowImport(false)}
          onComplete={load}
        />
      )}
    </div>
  );
}
